import React, {
  createContext,
  useContext,
  useReducer,
  useRef,
  useEffect,
  useMemo,
  ReactNode,
  useState,
  useCallback,
} from 'react'
import {
  areaManagementActions,
  areaManagementInitialState,
  areaManagementReducer,
  AreaManagementState,
} from '@/reducer'
import {
  searchAllAreas,
  searchAllSupervisors,
  searchAllAreaControllers,
  searchAllProvinces,
  searchAllBranches,
} from '@/api/index'
import useSWR from 'swr'
import useAxios from '@/utilities/useAxios'
import { ManagementTabType } from '@/utilities/types/filters'
import {
  AreaData,
  SupervisorData,
  AreaControllerData,
  ProvinceData,
  BranchData,
} from '@/utilities/types/table'

const DND_SERVICE = `${import.meta.env.VITE_DND_APP_BACKEND_URL}`
const local = `http://localhost:8082/api`

export type AreaManagementContextType = {
  state: AreaManagementState
  actions: typeof areaManagementActions
  dispatch: React.Dispatch<any>

  // Core Data
  provinces: ProvinceData[] | undefined
  areas: AreaData[] | undefined
  supervisors: SupervisorData[] | undefined
  areaControllers: AreaControllerData[] | undefined

  // Mutation Functions
  mutateProvinces: () => Promise<unknown>
  mutateAreas: () => Promise<unknown>
  mutateSupervisors: () => Promise<unknown>
  mutateAreaControllers: () => Promise<unknown>
  mutateSetupAreaSupervisors: () => Promise<unknown>
  mutateMaintenanceAdmins: () => Promise<unknown>

  // Tab
  selectedTab: ManagementTabType
  setSelectedTab: (tab: ManagementTabType) => void

  // Area Controllers
  optimizedSearchAreaControllers: (keyword: string) => void
  isAreaControllerLoading: boolean
  optimizedDateFilterAreaControllers: (
    startCreatedDate: string,
    endCreatedDate: string,
    startModifiedDate: string,
    endModifiedDate: string,
  ) => void
  optimizedFilterReset: () => void
  areaControllerkeyword: string
  areaControllerPage: number
  setAreaControllerPage: React.Dispatch<React.SetStateAction<number>>
  areaControllerRowsPerPage: number
  setAreaControllerRowsPerPage: React.Dispatch<React.SetStateAction<number>>
  areaControllerElem: number

  // Provinces
  optimizedSearchProvinces: (keyword: string) => void
  isProvincesLoading: boolean
  optimizedDateFilterProvinces: (
    startCreatedDate: string,
    endCreatedDate: string,
    startModifiedDate: string,
    endModifiedDate: string,
  ) => void
  optimizedFilterProvinceReset: () => void
  provinceskeyword: string
  provincesPage: number
  provincesRowsPerPage: number
  setProvincesPage: React.Dispatch<React.SetStateAction<number>>
  setProvincesRowsPerPage: React.Dispatch<React.SetStateAction<number>>
  provincesElem: number

  // Areas
  optimizedSearchAreas: (keyword: string) => void
  isAreasLoading: boolean
  optimizedDateFilterAreas: (
    startCreatedDate: string,
    endCreatedDate: string,
    startModifiedDate: string,
    endModifiedDate: string,
  ) => void
  optimizedFilterAreasReset: () => void
  areaskeyword: string
  areasPage: number
  areasRowsPerPage: number
  setAreasPage: React.Dispatch<React.SetStateAction<number>>
  setAreasRowsPerPage: React.Dispatch<React.SetStateAction<number>>
  areasElem: number

  // Supervisor
  optimizedSearchSupervisor: (keyword: string) => void
  isSupervisorLoading: boolean
  optimizedDateFilterSupervisor: (
    startCreatedDate: string,
    endCreatedDate: string,
    startModifiedDate: string,
    endModifiedDate: string,
  ) => void
  optimizedFilterSupervisorReset: () => void
  supervisorkeyword: string
  supervisorPage: number
  supervisorRowsPerPage: number
  setSupervisorPage: React.Dispatch<React.SetStateAction<number>>
  setSupervisorRowsPerPage: React.Dispatch<React.SetStateAction<number>>
  supervisorElem: number

  // Lists
  provincesList: ProvinceData[] | undefined
  branchesList: unknown[] | undefined
  areaControllerList: AreaControllerData[] | undefined

  // Sorting
  provincesSort: string
  areasSort: string
  supervisorSort: string
  areaControllerSort: string
  optimizedSortAreas: (sort: string) => void
  optimizedSortAreaController: (sort: string) => void
  optimizedSortSupervisor: (sort: string) => void
  optimizedSortProvince: (sort: string) => void

  // Maintenance Admin
  maintenanceAdminPage: number
  maintenanceAdminRowsPerPage: number
  maintenanceAdminSort: string
  maintenanceAdminKeyword: string
  maStartCreatedDate: string
  maEndCreatedDate: string
  maStartModifiedDate: string
  maEndModifiedDate: string
  maintenanceAdminElem: number
  isMaintenanceAdminLoading: boolean
  setMaintenanceAdminPage: React.Dispatch<React.SetStateAction<number>>
  setMaintenanceAdminRowsPerPage: React.Dispatch<React.SetStateAction<number>>

  // Setup Area Supervisor
  setupAreaSupervisorPage: number
  setupAreaSupervisorRowsPerPage: number
  setupAreaSupervisorSort: string
  setupAreaSupervisorKeyword: string
  sasStartCreatedDate: string
  sasEndCreatedDate: string
  sasStartModifiedDate: string
  sasEndModifiedDate: string
  setupAreaSupervisorElem: number
  isSetupAreaSupervisorLoading: boolean
  setSetupAreaSupervisorPage: React.Dispatch<React.SetStateAction<number>>
  setSetupAreaSupervisorRowsPerPage: React.Dispatch<
    React.SetStateAction<number>
  >
}

const AreaManagementContext = createContext<
  AreaManagementContextType | undefined
>(undefined)

export const useAreaManagementContext = (): AreaManagementContextType => {
  const context = useContext(AreaManagementContext)
  if (!context) {
    throw new Error(
      'useAreaManagementContext must be used within an AreaManagementContextProvider',
    )
  }
  return context
}

type AreaManagementContextProviderProps = {
  children: ReactNode
}

export const AreaManagementContextProvider: React.FC<
  AreaManagementContextProviderProps
> = ({ children }) => {
  const [selectedTab, setSelectedTab] = useState<ManagementTabType>('Area')

  //for area controller
  const [areaControllerPage, setAreaControllerPage] = useState(0)
  const [areaControllerRowsPerPage, setAreaControllerRowsPerPage] = useState(10)
  const [areaControllerSort, setAreaControllerSort] =
    useState('dateModified,asc')
  const [areaControllerkeyword, setAreaControllerKeyword] = useState('')
  const [acStartCreatedDate, setAcStartCreatedDate] = useState('')
  const [acEndCreatedDate, setAcEndCreatedDate] = useState('')
  const [acStartModifiedDate, setAcStartModifiedDate] = useState('')
  const [acEndModifiedDate, setAcEndModifiedDate] = useState('')
  const [areaControllerElem, setAreaControllerElem] = useState(0)
  const [isAreaControllerLoading, setIsAreaControllerLoading] = useState(false)

  //for provinces
  const [provincesPage, setProvincesPage] = useState(1)
  const [provincesRowsPerPage, setProvincesRowsPerPage] = useState(10)
  const [provincesSort, setProvincesSort] = useState('dateModified,asc')
  const [provinceskeyword, setProvincesKeyword] = useState('')
  const [pStartCreatedDate, setPStartCreatedDate] = useState('')
  const [pEndCreatedDate, setPEndCreatedDate] = useState('')
  const [pStartModifiedDate, setPStartModifiedDate] = useState('')
  const [pEndModifiedDate, setPEndModifiedDate] = useState('')
  const [provincesElem, setPronvincesElem] = useState(0)
  const [isProvincesLoading, setIsProvincesLoading] = useState(false)

  //for areas
  const [areasPage, setAreasPage] = useState(0)
  const [areasRowsPerPage, setAreasRowsPerPage] = useState(10)
  const [areasSort, setAreasSort] = useState('dateModified,asc')
  const [areaskeyword, setAreasKeyword] = useState('')
  const [aStartCreatedDate, setAStartCreatedDate] = useState('')
  const [aEndCreatedDate, setAEndCreatedDate] = useState('')
  const [aStartModifiedDate, setAStartModifiedDate] = useState('')
  const [aEndModifiedDate, setAEndModifiedDate] = useState('')
  const [areasElem, setAreasElem] = useState(0)
  const [isAreasLoading, setIsAreasLoading] = useState(false)
  const [allAreas, setAllAreas] = useState([])

  //for supervisor
  const [supervisorPage, setSupervisorPage] = useState(1)
  const [supervisorRowsPerPage, setSupervisorRowsPerPage] = useState(10)
  const [supervisorSort, setSupervisorSort] = useState('dateModified,asc')
  const [supervisorkeyword, setSupervisorKeyword] = useState('')
  const [sStartCreatedDate, setSStartCreatedDate] = useState('')
  const [sEndCreatedDate, setSEndCreatedDate] = useState('')
  const [sStartModifiedDate, setSStartModifiedDate] = useState('')
  const [sEndModifiedDate, setSEndModifiedDate] = useState('')
  const [supervisorElem, setSupervisorElem] = useState(0)
  const [isSupervisorLoading, setIsSupervisorLoading] = useState(false)

  //for Maintenance Admin
  const [maintenanceAdminPage, setMaintenanceAdminPage] = useState(0)
  const [maintenanceAdminRowsPerPage, setMaintenanceAdminRowsPerPage] =
    useState(10)
  const [maintenanceAdminSort, setMaintenanceAdminSort] =
    useState('dateModified,desc')
  const [maintenanceAdminKeyword, setMaintenanceAdminKeyword] = useState('')
  const [maStartCreatedDate, setMaStartCreatedDate] = useState('')
  const [maEndCreatedDate, setMaEndCreatedDate] = useState('')
  const [maStartModifiedDate, setMaStartModifiedDate] = useState('')
  const [maEndModifiedDate, setMaEndModifiedDate] = useState('')
  const [maintenanceAdminElem, setMaintenanceAdminElem] = useState(0)
  const [isMaintenanceAdminLoading, setIsMaintenanceAdminLoading] =
    useState(false)

  // for Setup Area Supervisor
  const [setupAreaSupervisorPage, setSetupAreaSupervisorPage] = useState(0)
  const [setupAreaSupervisorRowsPerPage, setSetupAreaSupervisorRowsPerPage] =
    useState(10)
  const [setupAreaSupervisorSort, setSetupAreaSupervisorSort] =
    useState('dateModified,desc')
  const [setupAreaSupervisorKeyword, setSetupAreaSupervisorKeyword] =
    useState('')
  const [sasStartCreatedDate, setSASStartCreatedDate] = useState('')
  const [sasEndCreatedDate, setSASEndCreatedDate] = useState('')
  const [sasStartModifiedDate, setSASStartModifiedDate] = useState('')
  const [sasEndModifiedDate, setSASEndModifiedDate] = useState('')
  const [setupAreaSupervisorElem, setSetupAreaSupervisorElem] = useState(0)
  const [isSetupAreaSupervisorLoading, setIsSetupAreaSupervisorLoading] =
    useState(false)

  const [regularFilters, setRegularFilters] = useState<any>(null)
  // const [branches, setBranches] = useState
  const [areaManagementState, dispatchAreaManagement] = useReducer(
    areaManagementReducer,
    areaManagementInitialState,
  )
  const areaControllerSorted = areaControllerSort.split(',')[0]
  const areaControllerOrder = areaControllerSort?.split(',')[1] || 'desc'
  const handleTabChange = useCallback((tab: ManagementTabType) => {
    setSelectedTab(tab)
    switch (tab) {
      case 'Province':
        setProvincesPage(1)
        setProvincesSort('date_modified,desc')
        setProvincesKeyword('')
        setPStartCreatedDate('')
        setPEndCreatedDate('')
        setPStartModifiedDate('')
        setPEndModifiedDate('')
        break
      case 'Area':
        setAreasPage(0)
        setAreasSort('date_modified,desc')
        setAreasKeyword('')
        setAStartCreatedDate('')
        setAEndCreatedDate('')
        setAStartModifiedDate('')
        setAEndModifiedDate('')
        break
      case 'Supervisor':
        setSupervisorPage(1)
        setSupervisorSort('dateModified,asc')
        setSupervisorKeyword('')
        setSStartCreatedDate('')
        setSEndCreatedDate('')
        setSStartModifiedDate('')
        setSEndModifiedDate('')
        break
      case 'Area Controller':
        setAreaControllerPage(0)
        setAreaControllerSort('date_modified,desc')
        setAreaControllerKeyword('')
        setAcStartCreatedDate('')
        setAcEndCreatedDate('')
        setAcStartModifiedDate('')
        setAcEndModifiedDate('')
        break
      case 'Maintenance Admin':
        setMaintenanceAdminPage(0)
        setMaintenanceAdminSort('date_modified,desc')
        setMaintenanceAdminKeyword('')
        setMaStartCreatedDate('')
        setMaEndCreatedDate('')
        setMaStartModifiedDate('')
        setMaEndModifiedDate('')
        break
      case 'Setup Area Supervisor':
        setSetupAreaSupervisorPage(0)
        setSetupAreaSupervisorSort('date_modified,desc')
        setSetupAreaSupervisorKeyword('')
        setSASStartCreatedDate('')
        setSASEndCreatedDate('')
        setSASStartModifiedDate('')
        setSASEndModifiedDate('')
        break
      default:
    }
  }, [])

  const getAllProvinces = async () => {
    setIsProvincesLoading(true)
    try {
      const response = await searchAllProvinces({
        keyword: provinceskeyword,
        startCreatedDate: pStartCreatedDate,
        endCreatedDate: pEndCreatedDate,
        startModifiedDate: pStartModifiedDate,
        endModifiedDate: pEndModifiedDate,
        page: provincesPage,
        size: provincesRowsPerPage,
        sort: provincesSort,
        pageable: {},
      })
      setPronvincesElem(response.data.data.totalElements)
      return response
    } catch (error) {
      console.error('Failed to fetch users:', error)
      throw error
    } finally {
      setIsProvincesLoading(false)
    }
  }
  const getAllAreas = async () => {
    setIsAreasLoading(true)
    try {
      const params: any = {
        keyword: areaskeyword,
        page: areasPage,
        size: areasRowsPerPage,
        sort: areasSort,
      }

      // ✅ Conditionally include date filters
      if (aStartCreatedDate) params.sinceCreated = aStartCreatedDate
      if (aEndCreatedDate) params.untilCreated = aEndCreatedDate
      if (aStartModifiedDate) params.sinceModified = aStartModifiedDate
      if (aEndModifiedDate) params.untilModified = aEndModifiedDate

      const response = await searchAllAreas(params)
      setAreasElem(response.data.totalElements)
      return response.data.content
    } catch (error) {
      console.error('Failed to fetch areas:', error)
      throw error
    } finally {
      setIsAreasLoading(false)
    }
  }

  const getAllSupervisors = async () => {
    setIsSupervisorLoading(true)
    try {
      const response = await searchAllSupervisors({
        keyword: supervisorkeyword,
        startCreatedDate: sStartCreatedDate,
        endCreatedDate: sEndCreatedDate,
        startModifiedDate: sStartModifiedDate,
        endModifiedDate: sEndModifiedDate,
        page: supervisorPage,
        size: supervisorRowsPerPage,
        sort: supervisorSort,
        pageable: {},
      })
      setSupervisorElem(response.data.totalElements)
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch users:', error)
      throw error
    } finally {
      setIsSupervisorLoading(false)
    }
  }

  const getAllAreaControllers = async () => {
    setIsAreaControllerLoading(true)
    try {
      const params: any = {
        keyword: areaControllerkeyword,
        page: areaControllerPage,
        size: areaControllerRowsPerPage,
        sort: areaControllerSorted,
        order: areaControllerOrder,
        roleName: selectedTab,
        pageable: {},
      }

      // 🧼 Conditionally include date filters
      if (acStartCreatedDate) params.sinceCreated = acStartCreatedDate
      if (acEndCreatedDate) params.untilCreated = acEndCreatedDate
      if (acStartModifiedDate) params.sinceModified = acStartModifiedDate
      if (acEndModifiedDate) params.untilModified = acEndModifiedDate

      const response = await searchAllAreaControllers(params)
      setAreaControllerElem(response.data.totalElements)
      return response.data.content
    } catch (error) {
      console.error('Failed to fetch users:', error)
      throw error
    } finally {
      setIsAreaControllerLoading(false)
    }
  }

  const getAllMaintenanceAdmins = async () => {
    setIsMaintenanceAdminLoading(true)
    try {
      const params: any = {
        keyword: maintenanceAdminKeyword,
        page: maintenanceAdminPage,
        size: areaControllerRowsPerPage,
        sort: maintenanceAdminSort.split(',')[0],
        order: maintenanceAdminSort.split(',')[1] || 'desc',
        roleName: 'Maintenance Admin',
        pageable: {},
      }

      if (maStartCreatedDate) params.sinceCreated = maStartCreatedDate
      if (maEndCreatedDate) params.untilCreated = maEndCreatedDate
      if (maStartModifiedDate) params.sinceModified = maStartModifiedDate
      if (maEndModifiedDate) params.untilModified = maEndModifiedDate

      const response = await searchAllAreaControllers(params)
      setMaintenanceAdminElem(response.data.totalElements)
      return response.data.content
    } catch (error) {
      console.error('Failed to fetch Maintenance Admin:', error)
      throw error
    } finally {
      setIsMaintenanceAdminLoading(false)
    }
  }

  // Fetch Setup Area Supervisor
  const getAllSetupAreaSupervisors = async () => {
    setIsSetupAreaSupervisorLoading(true)
    try {
      const params: any = {
        keyword: setupAreaSupervisorKeyword,
        page: setupAreaSupervisorPage,
        size: areaControllerRowsPerPage,
        sort: setupAreaSupervisorSort.split(',')[0],
        order: setupAreaSupervisorSort.split(',')[1] || 'desc',
        roleName: 'Setup Area Supervisor',
        pageable: {},
      }

      if (sasStartCreatedDate) params.sinceCreated = sasStartCreatedDate
      if (sasEndCreatedDate) params.untilCreated = sasEndCreatedDate
      if (sasStartModifiedDate) params.sinceModified = sasStartModifiedDate
      if (sasEndModifiedDate) params.untilModified = sasEndModifiedDate

      const response = await searchAllAreaControllers(params)
      setSetupAreaSupervisorElem(response.data.totalElements)
      return response.data.content
    } catch (error) {
      console.error('Failed to fetch Setup Area Supervisor:', error)
      throw error
    } finally {
      setIsSetupAreaSupervisorLoading(false)
    }
  }

  const getAllStaticProvinces = async () => {
    try {
      const response = await searchAllProvinces({
        keyword: '',
        startCreatedDate: '',
        endCreatedDate: '',
        startModifiedDate: '',
        endModifiedDate: '',
        page: 0,
        size: 999,
        sort: 'dateCreated,ASC',
        pageable: {},
      })
      return response.data.data.data
    } catch (error) {
      console.error('Failed to fetch users:', error)
      throw error
    }
  }

  const getAllStaticBranches = async (key: string) => {
    try {
      const response = await searchAllBranches({
        // branchID: undefined,           // optional
        // areaID: undefined,             // optional
        isMapped: false, // optional
        page: 0,
        size: 999,
        sort: 'name',
        order: 'asc',
      })

      console.log('data of branches', response.data.content)
      return response.data.content
    } catch (error) {
      console.error('Failed to fetch branches:', error)
      throw error
    }
  }

  const getAllStaticControllers = async () => {
    setIsAreaControllerLoading(true)
    try {
      const response = await searchAllAreaControllers({
        keyword: '',
        sinceCreated: '',
        untilCreated: '',
        sinceMOdified: '',
        untilModified: '',
        page: 0,
        size: 999,
        sort: 'dateCreated',
        order: 'ASC',
        roleName: selectedTab,
        pageable: {},
      })
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch users:', error)
      throw error
    }
  }

  const debounce = <T extends (...args: any[]) => any>(
    func: T,
  ): ((...args: Parameters<T>) => void) => {
    let timer: NodeJS.Timeout | null = null
    return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        timer = null
        func.apply(this, args)
      }, 500)
    }
  }

  const handleSearchAreaController = (keyword: string) => {
    setAreaControllerKeyword(keyword)
    setAreaControllerPage(0)
    useAxios
      .get(`${DND_SERVICE}/v2/user-area/search`, {
        params: {
          keyword: keyword,
          sinceCreated: acStartCreatedDate,
          untilCreated: acEndCreatedDate,
          sinceModified: acStartModifiedDate,
          untilModified: acEndModifiedDate,
          page: 0,
          size: areaControllerRowsPerPage,
          sort: areaControllerSort?.split(',')[0],
          order: areaControllerSort?.split(',')[1] || 'desc',
          pageable: {},
        },
      })
      .then((res) => res.data.content)
  }

  const handleSearchProvinces = (keyword: string) => {
    setProvincesKeyword(keyword)
    setProvincesPage(1)
    useAxios
      .get(`${DND_SERVICE}/v1/province/search`, {
        params: {
          keyword: keyword,
          startCreatedDate: pStartCreatedDate,
          endCreatedDate: pEndCreatedDate,
          startModifiedDate: pStartModifiedDate,
          endModifiedDate: pEndModifiedDate,
          page: 1,
          size: provincesRowsPerPage,
          sort: provincesSort,
          pageable: {},
        },
      })
      .then((res) => res.data)
  }

  const handleSearchAreas = (keyword: string) => {
    setAreasKeyword(keyword)
    setAreasPage(0)
    console.log('clicked')
    useAxios
      .get(`${DND_SERVICE}/v2/area/search`, {
        params: {
          keyword: keyword,
          sinceCreated: aStartCreatedDate,
          untilCreated: aEndCreatedDate,
          sinceModified: aStartModifiedDate,
          untilModified: aEndModifiedDate,
          page: 0,
          size: areasRowsPerPage,
          sort: areasSort.split(',')[0],
          order: areasSort.split(',')[1] || 'desc',
        },
      })
      .then((res) => res.data.content)
  }

  const handleSearchSupervisor = (keyword: string) => {
    setSupervisorKeyword(keyword)
    setSupervisorPage(1)
    useAxios
      .get(`${DND_SERVICE}/v1/area-supervisor/search`, {
        params: {
          keyword: keyword,
          sinceCreated: sStartCreatedDate,
          untilCreated: sEndCreatedDate,
          sinceModified: sStartModifiedDate,
          untilModified: sEndModifiedDate,
          page: 1,
          size: supervisorRowsPerPage,
          sort: supervisorSort,
          pageable: {},
        },
      })
      .then((res) => res.data)
  }

  const handleSortAreaController = (sort: string) => {
    setAreaControllerSort(sort)
    useAxios
      .get(`${DND_SERVICE}/v2/user-area/search`, {
        params: {
          keyword: areaControllerkeyword,
          sinceCreated: acStartCreatedDate,
          untilCreated: acEndCreatedDate,
          sinceModified: acStartModifiedDate,
          untilModified: acEndModifiedDate,
          page: areaControllerPage,
          size: areaControllerRowsPerPage,
          sort: areaControllerSorted,
          order: areaControllerOrder,
          roleName: selectedTab,
          pageable: {},
        },
      })
      .then((res) => res.data.content)
  }

  const handleSortAreas = (sort: string) => {
    setAreasSort(sort)
    useAxios
      .get(`${DND_SERVICE}/v2/area/search`, {
        params: {
          keyword: keyword,
          sinceCreated: aStartCreatedDate,
          untilCreated: aEndCreatedDate,
          sinceModified: aStartModifiedDate,
          until_modified: aEndModifiedDate,
          page: areasPage,
          size: areasRowsPerPage,
          sort: areasSort.split(',')[0],
          order: areasSort.split(',')[1] || 'desc',
        },
      })
      .then((res) => res.data)
  }

  const handleSortProvince = (sort: string) => {
    setProvincesSort(sort)
    useAxios
      .get(`${DND_SERVICE}/v1/province/search`, {
        params: {
          keyword: provinceskeyword,
          sinceCreated: pStartCreatedDate,
          untilCreated: pEndCreatedDate,
          sinceModified: pStartModifiedDate,
          untilModified: pEndModifiedDate,
          page: provincesPage,
          size: provincesRowsPerPage,
          sort: sort,
          pageable: {},
        },
      })
      .then((res) => res.data)
  }

  const handleSortSupervisor = (sort: string) => {
    setAreasSort(sort)
    useAxios
      .get(`${DND_SERVICE}/v1/area-supervisor/search`, {
        params: {
          keyword: supervisorkeyword,
          sinceCreated: sStartCreatedDate,
          untilCreated: sEndCreatedDate,
          sinceModified: sStartModifiedDate,
          untilModified: sEndModifiedDate,
          page: supervisorPage,
          size: supervisorRowsPerPage,
          sort: sort,
          pageable: {},
        },
      })
      .then((res) => res.data)
  }

  const handleSearchMaintenanceAdmin = (keyword: string) => {
    setMaintenanceAdminKeyword(keyword)
    setMaintenanceAdminPage(0)
    useAxios
      .get(`${DND_SERVICE}/v2/user-area/search`, {
        params: {
          keyword,
          sinceCreated: maStartCreatedDate,
          untilCreated: maEndCreatedDate,
          sinceModified: maStartModifiedDate,
          untilModified: maEndModifiedDate,
          page: 0,
          size: areaControllerRowsPerPage,
          sort: maintenanceAdminSort.split(',')[0],
          order: maintenanceAdminSort.split(',')[1] || 'desc',
          roleName: 'Maintenance Admin',
          pageable: {},
        },
      })
      .then((res) => res.data.content)
  }

  const handleSortMaintenanceAdmin = (sort: string) => {
    setMaintenanceAdminSort(sort)
    useAxios
      .get(`${DND_SERVICE}/v2/user-area/search`, {
        params: {
          keyword: maintenanceAdminKeyword,
          sinceCreated: maStartCreatedDate,
          untilCreated: maEndCreatedDate,
          sinceModified: maStartModifiedDate,
          untilModified: maEndModifiedDate,
          page: maintenanceAdminPage,
          size: areaControllerRowsPerPage,
          sort: sort.split(',')[0],
          order: sort.split(',')[1] || 'desc',
          roleName: 'Maintenance Admin',
          pageable: {},
        },
      })
      .then((res) => res.data.content)
  }
  const handleDateFilterMaintenanceAdmin = (
    sinceCreated: string,
    untilCreated: string,
    sinceModified: string,
    untilModified: string,
  ) => {
    setMaStartCreatedDate(sinceCreated)
    setMaEndCreatedDate(untilCreated)
    setMaStartModifiedDate(sinceModified)
    setMaEndModifiedDate(untilModified)
    setMaintenanceAdminPage(0)

    mutateMaintenanceAdmins()
  }

  const handleDateFilterAreaController = (
    sinceCreated: string,
    untilCreated: string,
    sinceModified: string,
    untilModified: string,
  ) => {
    setAcStartCreatedDate(sinceCreated)
    setAcEndCreatedDate(untilCreated)
    setAcStartModifiedDate(sinceModified)
    setAcEndModifiedDate(untilModified)
    setAreaControllerPage(0)

    mutateAreaControllers()
  }

  const handleDateFilterProvinces = (
    startCreatedDate: string,
    endCreatedDate: string,
    startModifiedDate: string,
    endModifiedDate: string,
  ) => {
    setPStartCreatedDate(startCreatedDate)
    setPEndCreatedDate(endCreatedDate)
    setPStartModifiedDate(startModifiedDate)
    setPEndModifiedDate(endModifiedDate)
    setProvincesPage(1)
    useAxios
      .get(`${DND_SERVICE}/v2/user-area/search`, {
        params: {
          keyword: provinceskeyword,
          sinceCreated: pStartCreatedDate,
          untilCreated: pEndCreatedDate,
          sinceModified: pStartModifiedDate,
          untilModified: pEndModifiedDate,
          page: 0,
          size: provincesRowsPerPage,
          sort: provincesSort,
          pageable: {},
        },
      })
      .then((res) => res.data.content)
  }

  const handleSearchSAS = (keyword: string) => {
    setSetupAreaSupervisorKeyword(keyword)
    setSetupAreaSupervisorPage(0)
    useAxios
      .get(`${DND_SERVICE}/v2/user-area/search`, {
        params: {
          keyword: setupAreaSupervisorKeyword,
          sinceCreated: sasStartCreatedDate,
          untilCreated: sasEndCreatedDate,
          sinceModified: sasStartModifiedDate,
          untilModified: sasEndModifiedDate,
          page: 0,
          size: areaControllerRowsPerPage,
          sort: setupAreaSupervisorSort.split(',')[0],
          order: setupAreaSupervisorSort.split(',')[1] || 'desc',
          roleName: 'Setup Area Supervisor',
          pageable: {},
        },
      })
      .then((res) => res.data.content)
  }

  const handleSortSAS = (sort: string) => {
    setSetupAreaSupervisorSort(sort)
    useAxios
      .get(`${DND_SERVICE}/v2/user-area/search`, {
        params: {
          keyword: setupAreaSupervisorKeyword,
          sinceCreated: sasStartCreatedDate,
          untilCreated: sasEndCreatedDate,
          sinceModified: sasStartModifiedDate,
          untilModified: sasEndModifiedDate,
          page: setupAreaSupervisorPage,
          size: areaControllerRowsPerPage,
          sort: sort.split(',')[0],
          order: sort.split(',')[1] || 'desc',
          roleName: 'Setup Area Supervisor',
          pageable: {},
        },
      })
      .then((res) => res.data.content)
  }

  const handleDateFilterSAS = (
    sinceCreated: string,
    untilCreated: string,
    sinceModified: string,
    untilModified: string,
  ) => {
    setSASStartCreatedDate(sinceCreated)
    setSASEndCreatedDate(untilCreated)
    setSASStartModifiedDate(sinceModified)
    setSASEndModifiedDate(untilModified)
    setSetupAreaSupervisorPage(0)

    mutateSetupAreaSupervisors()
  }

  const handleDateFilterAreas = (
    sinceCreated: string | null,
    untilCreated: string | null,
    sinceModified: string | null,
    untilModified: string | null,
  ) => {
    setAStartCreatedDate(sinceCreated)
    setAEndCreatedDate(untilCreated)
    setAStartModifiedDate(sinceModified)
    setAEndModifiedDate(untilModified)
    setAreasPage(0)

    mutateAreas()
  }

  const handleDateFilterSupervisor = (
    startCreatedDate: string,
    endCreatedDate: string,
    startModifiedDate: string,
    endModifiedDate: string,
  ) => {
    setSStartCreatedDate(startCreatedDate)
    setSEndCreatedDate(endCreatedDate)
    setSStartModifiedDate(startModifiedDate)
    setSEndModifiedDate(endModifiedDate)
    setSupervisorPage(1)
    useAxios
      .get(`${DND_SERVICE}/v1/area-supervisor/search`, {
        params: {
          keyword: supervisorkeyword,
          sinceCreated: sStartCreatedDate,
          untilCreated: sEndCreatedDate,
          sinceModified: sStartModifiedDate,
          untilModified: sEndModifiedDate,
          page: 1,
          size: supervisorRowsPerPage,
          sort: supervisorSort,
          pageable: {},
        },
      })
      .then((res) => res.data)
  }

  const handleFilterReset = () => {
    setAreaControllerKeyword('')
    setAcStartCreatedDate('')
    setAcEndCreatedDate('')
    setAcStartModifiedDate('')
    setAcEndModifiedDate('')
    setAreaControllerPage(0)
    setAreaControllerSort('date_modified,desc')
    useAxios
      .get(`${DND_SERVICE}/v2/user-area/search`, {
        params: {
          keyword: areaControllerkeyword,
          sinceCreated: acStartCreatedDate,
          untilCreated: acEndCreatedDate,
          sinceModified: acStartModifiedDate,
          untilModified: acEndModifiedDate,
          page: 0,
          size: areaControllerRowsPerPage,
          sort: 'date_modified',
          order: 'desc',
          roleName: selectedTab,
          pageable: {},
        },
      })
      .then((res) => res.data.content)
  }

  const handleFilterProvinceReset = () => {
    setProvincesKeyword('')
    setPStartCreatedDate('')
    setPEndCreatedDate('')
    setPStartModifiedDate('')
    setPEndModifiedDate('')
    setProvincesPage(1)
    setProvincesSort('dateModified,asc')
    useAxios
      .get(`${DND_SERVICE}/v2/user-area/search`, {
        params: {
          keyword: provinceskeyword,
          startCreatedDate: pStartCreatedDate,
          endCreatedDate: pEndCreatedDate,
          startModifiedDate: pStartModifiedDate,
          endModifiedDate: pEndModifiedDate,
          page: 0,
          size: provincesRowsPerPage,
          sort: 'dateModified',
          order: 'desc',
          pageable: {},
        },
      })
      .then((res) => res.data.content)
  }

  const handleFilterAreaReset = () => {
    setAreasKeyword('')
    setAStartCreatedDate('')
    setAEndCreatedDate('')
    setAStartModifiedDate('')
    setAEndModifiedDate('')
    setAreasPage(0)
    setAreasSort('dateModified,desc')
    useAxios
      .get(`${DND_SERVICE}/v2/area/search`, {
        params: {
          keyword: keyword,
          sinceCreated: aStartCreatedDate,
          untilCreated: aEndCreatedDate,
          sinceModified: aStartModifiedDate,
          untilModified: aEndModifiedDate,
          page: 0,
          size: areasRowsPerPage,
          sort: areasSort.split(',')[0],
          order: areasSort.split(',')[1] || 'desc',
        },
      })
      .then((res) => res.data)
  }
  const fetchAllAreas = async () => {
    try {
      const response = await useAxios.get(`${DND_SERVICE}/v2/area`)
      if (Array.isArray(response.data)) {
        setAllAreas(response.data)
      } else {
        console.error('Unexpected data shape:', response.data)
      }
    } catch (error) {
      console.error('Failed to fetch all areas:', error)
    }
  }

  const handleFilterSupervisorReset = () => {
    setSupervisorKeyword('')
    setSStartCreatedDate('')
    setSEndCreatedDate('')
    setSStartModifiedDate('')
    setSEndModifiedDate('')
    setSupervisorPage(1)
    setSupervisorSort('dateModified,asc')
    useAxios
      .get(`${DND_SERVICE}/v1/area-supervisor/search`, {
        params: {
          keyword: supervisorkeyword,
          startCreatedDate: sStartCreatedDate,
          endCreatedDate: sEndCreatedDate,
          startModifiedDate: sStartModifiedDate,
          endModifiedDate: sEndModifiedDate,
          page: 1,
          size: supervisorRowsPerPage,
          sort: supervisorSort,
          pageable: {},
        },
      })
      .then((res) => res.data)
  }

  const handleDeleteArea = async (areaID: number) => {
    try {
      await useAxios.delete(`${DND_SERVICE}/v2/area/${areaID}`)
      console.log('happening')
      mutateAreas()
      fetchAllAreas()
    } catch (err) {
      console.error('Delete area failed:', err)
    }
  }

  const createOptimizedCallback = <T extends (...args: any[]) => any>(
    func: T,
  ) => useCallback(debounce(func), [func])

  const optimizedCallbacks = {
    searchAreaControllers: createOptimizedCallback(handleSearchAreaController),
    dateFilterAreaControllers: createOptimizedCallback(
      handleDateFilterAreaController,
    ),
    filterReset: createOptimizedCallback(handleFilterReset),
    searchProvinces: createOptimizedCallback(handleSearchProvinces),
    dateFilterProvinces: createOptimizedCallback(handleDateFilterProvinces),
    filterProvinceReset: createOptimizedCallback(handleFilterProvinceReset),
    searchAreas: createOptimizedCallback(handleSearchAreas),
    dateFilterAreas: createOptimizedCallback(handleDateFilterAreas),
    filterAreasReset: createOptimizedCallback(handleFilterAreaReset),
    searchSupervisor: createOptimizedCallback(handleSearchSupervisor),
    dateFilterSupervisor: createOptimizedCallback(handleDateFilterSupervisor),
    filterSupervisorReset: createOptimizedCallback(handleFilterSupervisorReset),
    sortAreas: createOptimizedCallback(handleSortAreas),
    sortAreaController: createOptimizedCallback(handleSortAreaController),
    sortProvince: createOptimizedCallback(handleSortProvince),
    sortSupervisor: createOptimizedCallback(handleSortSupervisor),

    // Maintenance Admin
    searchMaintenanceAdmin: createOptimizedCallback(
      handleSearchMaintenanceAdmin,
    ),
    sortMaintenanceAdmin: createOptimizedCallback(handleSortMaintenanceAdmin),
    sortSAS: createOptimizedCallback(handleSortSAS),
    dateFilterMaintenanceAdmin: createOptimizedCallback(
      handleDateFilterMaintenanceAdmin,
    ),

    // Setup Area Supervisor
    searchSAS: createOptimizedCallback(handleSearchSAS),
    dateFilterSAS: createOptimizedCallback(handleDateFilterSAS),
  }

  const { data: provinces, mutate: mutateProvinces } = useSWR(
    selectedTab === 'Province' || 'Area'
      ? `/v1/province/search?keyword=${provinceskeyword}&sort=${provincesSort}&sinceCreated=${pStartCreatedDate}&untilCreated=${pEndCreatedDate}&sinceModified=${pStartModifiedDate}&untilModify=${pEndModifiedDate}&page=${provincesPage}&size=${provincesRowsPerPage}`
      : null,
    getAllProvinces,
    { revalidateOnFocus: false },
  )

  const { data: areas, mutate: mutateAreas } = useSWR(
    selectedTab === 'Area'
      ? `/v2/area/search?keyword=${areaskeyword}&sort=${areasSort}&sinceCreated=${aStartCreatedDate}&untilCreated=${aEndCreatedDate}&sinceModified=${aStartModifiedDate}&untilModify=${aEndModifiedDate}&page=${areasPage}&size=${areasRowsPerPage}`
      : null,
    getAllAreas,
    { revalidateOnFocus: true },
  )

  const { data: areaControllers, mutate: mutateAreaControllers } = useSWR(
    selectedTab === 'Area Controller'
      ? `/v2/user-area/search?keyword=${areaControllerkeyword}&sort=${areaControllerSorted}&Order=${areaControllerOrder}&sinceCreated=${acStartCreatedDate}&untilCreated=${acEndCreatedDate}&sinceModified=${acStartModifiedDate}&untilModified=${acEndModifiedDate}&page=${areaControllerPage}&size=${areaControllerRowsPerPage}&roleName=${selectedTab}`
      : null,
    getAllAreaControllers,
    { revalidateOnFocus: false },
  )

  // Maintenance Admin
  const { data: maintenanceAdmins, mutate: mutateMaintenanceAdmins } = useSWR(
    selectedTab === 'Maintenance Admin'
      ? `/v2/user-area/search?keyword=${maintenanceAdminKeyword}&sort=${maintenanceAdminSort}&sinceCreated=${maStartCreatedDate}&untilCreated=${maEndCreatedDate}&sinceModified=${maStartModifiedDate}&untilModified=${maEndModifiedDate}&page=${maintenanceAdminPage}&size=${areaControllerRowsPerPage}&roleName=Maintenance Admin`
      : null,
    getAllMaintenanceAdmins,
    { revalidateOnFocus: false },
  )

  // Setup Area Supervisor
  const { data: setupAreaSupervisors, mutate: mutateSetupAreaSupervisors } =
    useSWR(
      selectedTab === 'Setup Area Supervisor'
        ? `/v2/user-area/search?keyword=${setupAreaSupervisorKeyword}&sort=${setupAreaSupervisorSort}&sinceCreated=${sasStartCreatedDate}&untilCreated=${sasEndCreatedDate}&sinceModified=${sasStartModifiedDate}&untilModify=${sasEndModifiedDate}&page=${setupAreaSupervisorPage}&size=${areaControllerRowsPerPage}&roleName=Setup Area Supervisor`
        : null,
      getAllSetupAreaSupervisors,
      { revalidateOnFocus: false },
    )

  const { data: supervisors, mutate: mutateSupervisors } = useSWR(
    selectedTab === 'Supervisor'
      ? `/v1/area-supervisor/search?keyword=${supervisorkeyword}&sort=${supervisorSort}&startCreatedDate=${sStartCreatedDate}&endCreatedDate=${sEndCreatedDate}&startModifiedDate=${sStartModifiedDate}&endMofiedDate=${sEndModifiedDate}&page=${supervisorPage}&size=${supervisorRowsPerPage}`
      : null,
    getAllSupervisors,
    { revalidateOnFocus: false },
  )

  const { data: provincesList } = useSWR(
    `/v1/province/search`,
    getAllStaticProvinces,
    { revalidateOnFocus: false },
  )

  const { data: branchesList } = useSWR(
    `${DND_SERVICE}/v2/branch/search`,
    getAllStaticBranches,
    {
      revalidateOnFocus: false,
    },
  )

  const { data: areaControllerList } = useSWR(
    `${DND_SERVICE}/v2/user-area/search`,
    getAllStaticControllers,
    { revalidateOnFocus: false },
  )

  const stateRef = useRef<AreaManagementState>(areaManagementInitialState)

  useEffect(() => {
    stateRef.current = areaManagementState
  }, [areaManagementState])

  const value = useMemo(
    () => ({
      state: areaManagementState,
      actions: areaManagementActions,
      dispatch: dispatchAreaManagement,
      provinces,
      areas,
      supervisors,
      areaControllers,
      mutateProvinces,
      mutateAreas,
      mutateSupervisors,
      mutateAreaControllers,
      selectedTab,
      setSelectedTab: handleTabChange,
      regularFilters,
      setRegularFilters,
      optimizedSearchAreaControllers: optimizedCallbacks.searchAreaControllers,
      optimizedSearchMaintenanceAdmin:
        optimizedCallbacks.searchMaintenanceAdmin,
      optimizedSearchSetupAreaSupervisor: optimizedCallbacks.searchSAS,
      isAreaControllerLoading,
      optimizedDateFilterAreaControllers:
        optimizedCallbacks.dateFilterAreaControllers,
      optimizedFilterReset: optimizedCallbacks.filterReset,
      areaControllerkeyword,
      areaControllerPage,
      areaControllerRowsPerPage,
      setAreaControllerPage,
      setAreaControllerRowsPerPage,
      areaControllerElem,
      optimizedSearchProvinces: optimizedCallbacks.searchProvinces,
      isProvincesLoading,
      optimizedDateFilterProvinces: optimizedCallbacks.dateFilterProvinces,
      optimizedFilterProvinceReset: optimizedCallbacks.filterProvinceReset,

      optimizedDateFilterMaintenanceAdmin:
        optimizedCallbacks.dateFilterMaintenanceAdmin,
      optimizedDateFilterSetupAreaSupervisor: optimizedCallbacks.dateFilterSAS,

      provinceskeyword,
      provincesPage,
      provincesRowsPerPage,
      setProvincesPage,
      setProvincesRowsPerPage,
      provincesElem,
      optimizedSearchAreas: optimizedCallbacks.searchAreas,
      isAreasLoading,
      optimizedDateFilterAreas: optimizedCallbacks.dateFilterAreas,
      optimizedFilterAreasReset: optimizedCallbacks.filterAreasReset,
      areaskeyword,
      areasPage,
      areasRowsPerPage,
      setAreasPage,
      setAreasRowsPerPage,
      areasElem,
      optimizedSearchSupervisor: optimizedCallbacks.searchSupervisor,
      isSupervisorLoading,
      optimizedDateFilterSupervisor: optimizedCallbacks.dateFilterSupervisor,
      optimizedFilterSupervisorReset: optimizedCallbacks.filterSupervisorReset,
      supervisorkeyword,
      supervisorPage,
      supervisorRowsPerPage,
      setSupervisorPage,
      setSupervisorRowsPerPage,
      supervisorElem,
      provincesList,
      areaControllerList,
      areaControllerSort,
      provincesSort,
      areasSort,
      supervisorSort,
      optimizedSortAreas: optimizedCallbacks.sortAreas,
      optimizedSortAreaController: optimizedCallbacks.sortAreaController,
      optimizedSortProvince: optimizedCallbacks.sortProvince,
      optimizedSortSupervisor: optimizedCallbacks.sortSupervisor,
      optimizedSortMaintenanceAdmin: optimizedCallbacks.sortMaintenanceAdmin,
      optimizedSortSAS: optimizedCallbacks.sortSAS,

      maintenanceAdminPage,
      maintenanceAdminRowsPerPage,
      maintenanceAdminSort,
      maintenanceAdminKeyword,
      maStartCreatedDate,
      maEndCreatedDate,
      maStartModifiedDate,
      maEndModifiedDate,
      maintenanceAdminElem,
      isMaintenanceAdminLoading,
      setupAreaSupervisorPage,
      setupAreaSupervisorRowsPerPage,
      setupAreaSupervisorSort,
      setupAreaSupervisorKeyword,
      sasStartCreatedDate,
      sasEndCreatedDate,
      sasStartModifiedDate,
      sasEndModifiedDate,
      setupAreaSupervisorElem,
      isSetupAreaSupervisorLoading,
      setMaintenanceAdminPage,
      setMaintenanceAdminRowsPerPage,
      setSetupAreaSupervisorPage,
      setSetupAreaSupervisorRowsPerPage,
      branchesList,
      setupAreaSupervisors,
      mutateSetupAreaSupervisors,
      maintenanceAdmins,
      mutateMaintenanceAdmins,
      allAreas,
      fetchAllAreas,
      handleDeleteArea,
    }),
    [
      areaManagementState,
      provinces,
      areas,
      supervisors,
      areaControllers,
      mutateProvinces,
      mutateAreas,
      mutateSupervisors,
      mutateAreaControllers,
      selectedTab,
      handleTabChange,
      regularFilters,
      optimizedCallbacks.searchAreaControllers,
      optimizedCallbacks.dateFilterAreaControllers,
      optimizedCallbacks.filterReset,
      optimizedCallbacks.searchProvinces,
      optimizedCallbacks.dateFilterProvinces,
      optimizedCallbacks.filterProvinceReset,
      optimizedCallbacks.searchAreas,
      optimizedCallbacks.dateFilterAreas,
      optimizedCallbacks.filterAreasReset,
      optimizedCallbacks.searchSupervisor,
      optimizedCallbacks.dateFilterSupervisor,
      optimizedCallbacks.filterSupervisorReset,
      optimizedCallbacks.sortAreas,
      optimizedCallbacks.sortAreaController,
      optimizedCallbacks.sortProvince,
      optimizedCallbacks.sortSupervisor,
      optimizedCallbacks.sortMaintenanceAdmin,
      optimizedCallbacks.sortSAS,
      optimizedCallbacks.searchMaintenanceAdmin,
      optimizedCallbacks.searchSAS,
      optimizedCallbacks.dateFilterMaintenanceAdmin,
      optimizedCallbacks.dateFilterSAS,
      isAreaControllerLoading,
      areaControllerkeyword,
      areaControllerPage,
      areaControllerRowsPerPage,
      areaControllerElem,
      isProvincesLoading,
      provinceskeyword,
      provincesPage,
      provincesRowsPerPage,
      provincesElem,
      isAreasLoading,
      areaskeyword,
      areasPage,
      areasRowsPerPage,
      areasElem,
      isSupervisorLoading,
      supervisorkeyword,
      supervisorPage,
      supervisorRowsPerPage,
      supervisorElem,
      provincesList,
      areaControllerList,
      areaControllerSort,
      provincesSort,
      areasSort,
      supervisorSort,
      maintenanceAdminPage,
      maintenanceAdminRowsPerPage,
      maintenanceAdminSort,
      maintenanceAdminKeyword,
      maStartCreatedDate,
      maEndCreatedDate,
      maStartModifiedDate,
      maEndModifiedDate,
      maintenanceAdminElem,
      isMaintenanceAdminLoading,
      setupAreaSupervisorPage,
      setupAreaSupervisorRowsPerPage,
      setupAreaSupervisorSort,
      setupAreaSupervisorKeyword,
      sasStartCreatedDate,
      sasEndCreatedDate,
      sasStartModifiedDate,
      sasEndModifiedDate,
      setupAreaSupervisorElem,
      isSetupAreaSupervisorLoading,
      branchesList,
      setupAreaSupervisors,
      mutateSetupAreaSupervisors,
      maintenanceAdmins,
      mutateMaintenanceAdmins,
      allAreas,
      fetchAllAreas,
      handleDeleteArea,
    ],
  )

  return (
    <AreaManagementContext.Provider value={value}>
      {children}
    </AreaManagementContext.Provider>
  )
}
